import { useAuth0 } from '@auth0/auth0-react';
import { registerAuthBridge } from '@sunstone/api-client';
import { sessionExpired as sessionExpiredAction } from '@sunstone/store';
import { useEffect } from 'react';
import { useAppDispatch } from '@/store';
import { syncAuth0Claims, syncAuth0Logout } from '../../store/middleware/authSync';
import { useAuthClaims } from './useAuthClaims';

/**
 * Hook to automatically sync Auth0 authentication state with Redux store
 *
 * This hook should be used at the root level of your application (e.g., in App.tsx)
 * to ensure that Auth0 authentication state is always synced with Redux.
 *
 * It listens for Auth0 authentication changes and dispatches sync actions
 * to update the Redux store with the latest user claims.
 *
 * @example
 * ```tsx
 * function App() {
 *   useAuth0Sync() // Sync Auth0 with Redux
 *
 *   return <YourAppComponents />
 * }
 * ```
 */
export function useAuth0Sync() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const { claims, isLoading, error } = useAuthClaims();

  // Wire the Connect transport's auth bridge once, so a stale/expired token
  // hit on any RPC call (not just the initial claims sync below) can
  // silently refresh via getAccessTokenSilently, or surface as
  // state.auth.sessionExpired when refresh itself fails.
  useEffect(() => {
    if (import.meta.env.VITE_SKIP_AUTH === 'true') return;

    return registerAuthBridge({
      refreshToken: async () => {
        const accessToken = await getAccessTokenSilently();
        if (!accessToken) {
          throw new Error('Missing Refresh Token');
        }
        return accessToken;
      },
      onSessionExpired: () => dispatch(sessionExpiredAction()),
    });
  }, [getAccessTokenSilently, dispatch]);

  useEffect(() => {
    // Skip all Auth0 syncing in test mode — auth state is pre-seeded via preloadedState
    if (import.meta.env.VITE_SKIP_AUTH === 'true') return;

    // If user is authenticated and claims are loaded, sync to Redux
    if (isAuthenticated && !isLoading && claims && !error) {
      dispatch(syncAuth0Claims(claims));
    }

    // If session has expired (token retrieval failed), dispatch logout and redirect to login.
    // Auth0 may still report isAuthenticated=true (it knows who you are but can't refresh),
    // so we must call loginWithRedirect here — AuthGuard won't trigger it in this state.
    if (error === 'SESSION_EXPIRED' && !isLoading) {
      dispatch(syncAuth0Logout());
      dispatch(sessionExpiredAction());
      loginWithRedirect();
    }

    // If user is not authenticated, dispatch logout to clear Redux state
    if (!isAuthenticated && !isLoading) {
      dispatch(syncAuth0Logout());
    }
  }, [isAuthenticated, isLoading, claims, error, dispatch, loginWithRedirect]);

  return {
    isSyncing: isLoading,
    syncError: error,
  };
}
